import React, { forwardRef } from "react";
import PropTypes from "prop-types";
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, Colors, PointElement, LineElement, LineController, BarController } from "chart.js";
import { Chart } from "react-chartjs-2";
import { scoreLevels } from "../../context/global";
import { getDesignBackgroundColor } from "../../utils/chartPatterns";

const ScoreGraph = forwardRef(({ variants, initData, designs = [], kpi }, ref) => {
    ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, Colors, PointElement, LineElement, LineController, BarController);

    const extraScoreLevels = [{ value: 0, label: "Geen score" }, ...scoreLevels];

    const footer = (tooltipItems) => {
        let str = "";
        tooltipItems.forEach(function (tooltipItem) {
            str += "Score: " + scoreLevels[Number(tooltipItem.parsed.y) - 1].label;
        });
        return str;
    };

    // Separate goal values from design values and create standardized datasets
    const standardizedData = initData.map((dataset) => {
        const goalValue = dataset.data[0]; // First value is always the goal
        const designValues = dataset.data.slice(1); // Rest are design values
        
        return [
            // Goal as a line
            {
                label: "Doel",
                data: Array(variants.length).fill(goalValue),
                type: "line",
                borderWidth: 3,
                borderColor: 'red',
                backgroundColor: 'red',
                legendColor: 'red',
                pointStyle: false,
                borderDash: [8, 8],
                order: 1, // Lines appear on top
            },
            // Design values as bars
            {
                label: dataset.label,
                data: designValues,
                type: "bar",
                backgroundColor: designValues.map((value, i) => {
                    // Use striped pattern for reference designs
                    const design = designs[i] || { is_reference: i === 0 }; // Fallback for backward compatibility
                    return getDesignBackgroundColor(design, dataset.backgroundColor[0]);
                }),
                // Store the original solid color for legend
                legendColor: dataset.backgroundColor,
                borderWidth: 2,
                order: 2, // Bars appear below lines
            }
        ];
    }).flat();

    const options = {
        elements: {
            bar: {
                borderWidth: 2,
            },
        },
        responsive: true,
        plugins: {
            tooltip: {
                callbacks: {
                    footer: footer,
                },
            },
            legend: {
                display: true,
                position: "chartArea",
                align: "start",
                labels: {
                    filter: function(legendItem) {
                        return legendItem.text === "Doel";
                    },
                    generateLabels: function(chart) {
                        const datasets = chart.data.datasets;
                        
                        return datasets.map((dataset, i) => {
                            const isLine = dataset.type === 'line';
                            const color = dataset.legendColor || (Array.isArray(dataset.backgroundColor) ? dataset.backgroundColor[0] : dataset.backgroundColor);
                            return {
                                text: dataset.label,
                                fillStyle: isLine ? 'transparent' : color,
                                strokeStyle: color,
                                lineWidth: isLine ? 3 : 0,
                                lineDash: isLine && dataset.borderDash ? dataset.borderDash : [],
                                hidden: !chart.isDatasetVisible(i),
                                index: i,
                        }
                    })
                    }
                }
            },
            title: {
                display: true,
                text: kpi.name,
                font: {
                    size: 14,
                }
            },
        },
        scales: {
            y: {
                ticks: {
                    callback: function (val, index) {
                        return extraScoreLevels[index].label;
                    },
                    beginAtZero: false,
                    stepSize: 1,
                    max: 5,
                },
                min: 0,
                max: 5,
            },
        },
    };

    const data = {
        labels: variants,
        datasets: standardizedData,
    };
    if (initData === null) {
        return null;
    }

    return <Chart ref={ref} type="bar" options={options} data={data} redraw />;
});

ScoreGraph.displayName = 'ScoreGraph';

ScoreGraph.propTypes = {
    variants: PropTypes.array.isRequired,
    initData: PropTypes.array.isRequired,
    designs: PropTypes.array,
};

export default ScoreGraph;
